import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

export type ApprovalRequestType =
  | "rate_change" | "low_margin" | "retainage_release"
  | "change_order" | "void_record" | "invoice_approve" | "bill_approve"
  | "draw_approve" | "backcharge" | "reopen_wo" | "vendor_approve" | "other";

export type ApprovalStatus =
  | "pending" | "approved" | "rejected"
  | "info_requested" | "forwarded" | "withdrawn" | "expired";

export interface ApprovalRequest {
  id: string;
  company_id: string;
  project_id: string | null;
  request_type: ApprovalRequestType;
  urgency: string;
  title: string;
  description: string | null;
  linked_wo_id: string | null;
  linked_draw_id: string | null;
  linked_invoice_id: string | null;
  linked_sov_line_id: string | null;
  linked_bill_id: string | null;
  request_data: Record<string, any>;
  requested_by: string;
  requested_at: string;
  approval_mode: string;
  status: ApprovalStatus;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
  updated_at: string;
  customer_visible?: boolean;
  // joined
  projects?: { name: string } | null;
  requester_profile?: { first_name: string; last_name: string; display_name: string } | null;
  linked_bill?: {
    bill_number: string;
    bill_date: string;
    due_date: string | null;
    status: string;
    subtotal: number;
    total: number;
    description: string | null;
    vendors?: { name: string } | null;
  } | null;
  linked_draw?: {
    id: string;
    draw_number: number;
    status: string;
    subtotal: number;
    retainage_amount: number;
    net_due: number;
    created_at: string;
    approved_at: string | null;
  } | null;
}

export interface ApprovalAssignee {
  id: string;
  request_id: string;
  user_id: string;
  status: string;
  decision_note: string | null;
  forwarded_to: string | null;
  decided_at: string | null;
  created_at: string;
  user_profile?: { first_name: string; last_name: string; display_name: string } | null;
}

export interface ApprovalAuditEntry {
  id: string;
  request_id: string;
  actor_id: string;
  action: string;
  note: string | null;
  metadata: Record<string, any>;
  created_at: string;
  actor_profile?: { first_name: string; last_name: string; display_name: string } | null;
}

export interface ApprovalRequestStep {
  id: string;
  request_id: string;
  step_order: number;
  approver_user_id: string;
  status: "pending" | "approved" | "rejected" | "forwarded" | "skipped";
  decision_note: string | null;
  decided_at: string | null;
  created_at: string;
  approver_profile?: { first_name: string | null; last_name: string | null; display_name: string | null } | null;
}

export interface ApprovalRequestEvent {
  id: string;
  request_id: string;
  actor_id: string;
  event_type: string;
  previous_state: string | null;
  new_state: string | null;
  comment: string | null;
  metadata: Record<string, any>;
  created_at: string;
  actor_profile?: { first_name: string | null; last_name: string | null; display_name: string | null } | null;
}

// ─── Approval steps for a request (ordered approvers) ───
export const useApprovalSteps = (requestId: string | null) => {
  return useQuery({
    queryKey: ["approval_steps", requestId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("approval_request_steps" as any) as any)
        .select("*")
        .eq("request_id", requestId!)
        .order("step_order");
      if (error) throw error;

      const steps = (data ?? []) as ApprovalRequestStep[];
      const userIds = steps.map((s) => s.approver_user_id).filter(Boolean);
      if (userIds.length === 0) return steps;

      const { data: profiles } = await supabase
        .from("user_profiles")
        .select("user_id, first_name, last_name, display_name")
        .in("user_id", userIds);
      const profileMap: Record<string, any> = {};
      (profiles ?? []).forEach((p: any) => { profileMap[p.user_id] = p; });

      return steps.map((s) => ({
        ...s,
        approver_profile: profileMap[s.approver_user_id] ?? null,
      })) as ApprovalRequestStep[];
    },
    enabled: !!requestId,
  });
};

// ─── Approval events for a request (full history) ───
export const useApprovalEvents = (requestId: string | null) => {
  return useQuery({
    queryKey: ["approval_events", requestId],
    queryFn: async () => {
      // Try new approval_request_events table first, fall back to approval_audit
      const { data: eventsData, error: eventsErr } = await (supabase
        .from("approval_request_events" as any) as any)
        .select("*")
        .eq("request_id", requestId!)
        .order("created_at");

      let events: ApprovalRequestEvent[] = [];

      if (!eventsErr && eventsData && eventsData.length > 0) {
        events = eventsData as ApprovalRequestEvent[];
      } else {
        // Fallback: map approval_audit to same shape
        const { data: auditData } = await supabase
          .from("approval_audit")
          .select("*")
          .eq("request_id", requestId!)
          .order("created_at");
        events = (auditData ?? []).map((a: any) => ({
          id: a.id,
          request_id: a.request_id,
          actor_id: a.actor_id,
          event_type: a.action,
          previous_state: a.prev_status ?? null,
          new_state: a.new_status ?? null,
          comment: a.note ?? null,
          metadata: a.metadata ?? {},
          created_at: a.created_at,
        })) as ApprovalRequestEvent[];
      }

      if (events.length === 0) return events;

      const actorIds = [...new Set(events.map((e) => e.actor_id).filter(Boolean))];
      const { data: profiles } = await supabase
        .from("user_profiles")
        .select("user_id, first_name, last_name, display_name")
        .in("user_id", actorIds);
      const profileMap: Record<string, any> = {};
      (profiles ?? []).forEach((p: any) => { profileMap[p.user_id] = p; });

      return events.map((e) => ({
        ...e,
        actor_profile: profileMap[e.actor_id] ?? null,
      })) as ApprovalRequestEvent[];
    },
    enabled: !!requestId,
  });
};

// ─── Pending count for badge ───
export const useApprovalPendingCount = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("approval-badge")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "approval_assignees",
        filter: `user_id=eq.${user.id}`,
      }, () => {
        qc.invalidateQueries({ queryKey: ["approval_pending_count"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, qc]);

  return useQuery({
    queryKey: ["approval_pending_count", user?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("approval_assignees")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user!.id)
        .eq("status", "pending");
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
    refetchInterval: 30000,
  });
};

// ─── All approval requests ───
export const useApprovalRequests = (tab: "pending" | "all" | "sent" | "completed", pagination?: import("./usePagination").PaginationState) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["approval_requests", tab, user?.id, pagination?.page],
    queryFn: async () => {
      if (tab === "pending") {
        const { data: assigneeIds } = await supabase
          .from("approval_assignees")
          .select("request_id")
          .eq("user_id", user!.id)
          .eq("status", "pending");
        const ids = (assigneeIds ?? []).map((a: any) => a.request_id);
        if (ids.length === 0) { pagination?.setTotalCount(0); return []; }

        if (pagination) {
          pagination.setTotalCount(ids.length);
        }

        let query = supabase
          .from("approval_requests")
          .select("*, projects(name), linked_bill:bills(bill_number, total, vendors(name))")
          .in("id", ids)
          .order("created_at", { ascending: false });

        if (pagination) query = query.range(pagination.from, pagination.to);
        const { data, error } = await query;
        if (error) throw error;
        return (data ?? []) as unknown as ApprovalRequest[];
      }

      // For other tabs, get count first
      let countQuery = supabase.from("approval_requests").select("id", { count: "exact", head: true });
      if (tab === "sent") countQuery = countQuery.eq("requested_by", user!.id);
      else if (tab === "completed") countQuery = countQuery.in("status", ["approved", "rejected", "withdrawn", "expired"]);

      if (pagination) {
        const { count } = await countQuery;
        pagination.setTotalCount(count ?? 0);
      }

      let query = supabase
        .from("approval_requests")
        .select("*, projects(name), linked_bill:bills(bill_number, total, vendors(name))")
        .order("created_at", { ascending: false });

      if (tab === "sent") query = query.eq("requested_by", user!.id);
      else if (tab === "completed") query = query.in("status", ["approved", "rejected", "withdrawn", "expired"]);

      if (pagination) query = query.range(pagination.from, pagination.to);
      else query = query.limit(200);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as ApprovalRequest[];
    },
    enabled: !!user,
  });
};

// ─── Single request detail ───
export const useApprovalDetail = (requestId: string | null) => {
  return useQuery({
    queryKey: ["approval_detail", requestId],
    queryFn: async () => {
      const [reqRes, assigneesRes, auditRes] = await Promise.all([
        supabase
          .from("approval_requests")
          .select("*, projects(name), linked_bill:bills(bill_number, bill_date, due_date, status, subtotal, total, description, vendors(name)), linked_draw:wo_draws(id, draw_number, status, subtotal, retainage_amount, net_due, created_at, approved_at)")
          .eq("id", requestId!)
          .single(),
        supabase
          .from("approval_assignees")
          .select("*")
          .eq("request_id", requestId!)
          .order("created_at"),
        supabase.from("approval_audit").select("*").eq("request_id", requestId!).order("created_at"),
      ]);
      if (reqRes.error) throw reqRes.error;

      // Secondary lookup: fetch user_profiles for all assignee user_ids
      const assignees = (assigneesRes.data ?? []) as ApprovalAssignee[];
      const userIds = assignees.map((a) => a.user_id).filter(Boolean);
      let profileMap: Record<string, { first_name: string | null; last_name: string | null; display_name: string | null }> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("user_profiles")
          .select("user_id, first_name, last_name, display_name")
          .in("user_id", userIds);
        (profiles ?? []).forEach((p: any) => {
          profileMap[p.user_id] = { first_name: p.first_name, last_name: p.last_name, display_name: p.display_name };
        });
      }

      // Attach profile data to each assignee
      const assigneesWithProfiles = assignees.map((a) => ({
        ...a,
        user_profile: profileMap[a.user_id] ?? null,
      }));

      return {
        request: reqRes.data as unknown as ApprovalRequest,
        assignees: assigneesWithProfiles as ApprovalAssignee[],
        audit: (auditRes.data ?? []) as ApprovalAuditEntry[],
      };
    },
    enabled: !!requestId,
  });
};

// ─── Stats ───
export const useApprovalStats = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["approval_stats", user?.id],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const [pending, approvedToday, rejected, forwarded] = await Promise.all([
        supabase.from("approval_assignees").select("*", { count: "exact", head: true })
          .eq("user_id", user!.id).eq("status", "pending"),
        supabase.from("approval_assignees").select("*", { count: "exact", head: true })
          .eq("user_id", user!.id).eq("status", "approved").gte("decided_at", today),
        supabase.from("approval_assignees").select("*", { count: "exact", head: true })
          .eq("user_id", user!.id).eq("status", "rejected"),
        supabase.from("approval_assignees").select("*", { count: "exact", head: true })
          .eq("user_id", user!.id).eq("status", "forwarded"),
      ]);
      return {
        pending: pending.count ?? 0,
        approvedToday: approvedToday.count ?? 0,
        rejected: rejected.count ?? 0,
        forwarded: forwarded.count ?? 0,
      };
    },
    enabled: !!user,
  });
};

// ─── Mutations ───
export const useApprovalActions = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["approval_requests"] });
    qc.invalidateQueries({ queryKey: ["approval_detail"] });
    qc.invalidateQueries({ queryKey: ["approval_pending_count"] });
    qc.invalidateQueries({ queryKey: ["approval_stats"] });
    qc.invalidateQueries({ queryKey: ["approval_steps"] });
    qc.invalidateQueries({ queryKey: ["approval_events"] });
  };

  const getCompanyId = async () => {
    const { data } = await supabase
      .from("user_profiles")
      .select("company_id")
      .eq("user_id", user!.id)
      .single();
    return data?.company_id;
  };

  const decide = useMutation({
    mutationFn: async (args: {
      assigneeId: string;
      requestId: string;
      status: "approved" | "rejected" | "info_requested";
      note?: string;
    }) => {
      const companyId = await getCompanyId();

      // Self-approval guard: requester cannot approve their own request
      const { data: req } = await supabase
        .from("approval_requests")
        .select("requested_by, request_type, title, linked_bill_id, linked_wo_id, linked_draw_id, status")
        .eq("id", args.requestId)
        .single();
      if (req?.requested_by && req.requested_by === user!.id && args.status === "approved") {
        throw new Error("You cannot approve a request that you submitted.");
      }

      const prevStatus = (req as any)?.status ?? "pending";

      // Update assignee
      const { error: aeErr } = await supabase
        .from("approval_assignees")
        .update({
          status: args.status,
          decision_note: args.note ?? null,
          decided_at: new Date().toISOString(),
        })
        .eq("id", args.assigneeId);
      if (aeErr) throw aeErr;

      // Mark all OTHER pending assignees on this request as "skipped" so they
      // don't remain in anyone's queue after the request is decided.
      try {
        await supabase
          .from("approval_assignees")
          .update({
            status: "withdrawn",
            decision_note: `Auto-closed: request ${args.status} by another approver`,
            decided_at: new Date().toISOString(),
          })
          .eq("request_id", args.requestId)
          .eq("status", "pending")
          .neq("id", args.assigneeId);
      } catch { /* non-blocking */ }

      // Update approval_requests status — critical, must succeed
      const { error: arErr } = await supabase
        .from("approval_requests")
        .update({ status: args.status, resolved_at: new Date().toISOString() } as any)
        .eq("id", args.requestId);
      if (arErr) throw arErr;

      // Set resolution metadata — non-blocking (columns may not exist in older deployments)
      try {
        await supabase
          .from("approval_requests")
          .update({
            resolved_by: user!.id,
            resolved_at: new Date().toISOString(),
            resolution_note: args.note ?? null,
          } as any)
          .eq("id", args.requestId);
      } catch {
        // resolved_by / resolved_at columns added in migration 20260306120000
        // Safe to skip if migration not yet applied
      }

      // Write to approval_request_events (new system)
      try {
        await (supabase.from("approval_request_events" as any) as any).insert({
          company_id: companyId,
          request_id: args.requestId,
          actor_id: user!.id,
          event_type: args.status,
          previous_state: prevStatus,
          new_state: args.status,
          comment: args.note ?? null,
          metadata: {
            request_type: (req as any)?.request_type,
            title: (req as any)?.title,
            linked_bill_id: (req as any)?.linked_bill_id,
            assignee_id: user!.id,
          },
        } as any);
      } catch {
        // Table added in migration 20260306220000 — skip if not yet applied
      }

      // Audit — write to approval_audit with full metadata
      await supabase.from("approval_audit").insert({
        company_id: companyId,
        request_id: args.requestId,
        actor_id: user!.id,
        action: args.status,
        note: args.note,
        prev_status: prevStatus,
        new_status: args.status,
        metadata: {
          request_type: (req as any)?.request_type,
          title: (req as any)?.title,
          linked_bill_id: (req as any)?.linked_bill_id,
          linked_wo_id: (req as any)?.linked_wo_id,
          linked_draw_id: (req as any)?.linked_draw_id,
          decision_note: args.note,
          assignee_id: user!.id,
        },
      } as any);

      // Also write to ops_audit_log for cross-system audit trail
      try {
        await supabase.from("ops_audit_log").insert({
          table_name: "approval_requests",
          record_id: args.requestId,
          action: args.status === "approved" ? "approval_granted" : "approval_rejected",
          prev_status: prevStatus,
          new_status: args.status,
          changed_by: user!.id,
          metadata: {
            request_type: (req as any)?.request_type,
            title: (req as any)?.title,
            linked_bill_id: (req as any)?.linked_bill_id,
            linked_wo_id: (req as any)?.linked_wo_id,
            decision_note: args.note,
            assignee_id: user!.id,
          },
        } as any);
      } catch {
        // Don't block main action if ops_audit_log write fails
      }

      // ── Cascade decision to the linked bill ──────────────────────────
      const linkedBillId = (req as any)?.linked_bill_id;
      if (linkedBillId) {
        if (args.status === "approved") {
          await supabase
            .from("bills")
            .update({ status: "approved", approved_at: new Date().toISOString(), approved_by: user!.id } as any)
            .eq("id", linkedBillId);
          try {
            await supabase.from("ops_audit_log").insert({
              table_name: "bills", record_id: linkedBillId,
              action: "bill_approved_via_approval", prev_status: "sent", new_status: "approved",
              changed_by: user!.id,
              metadata: { approval_request_id: args.requestId },
            } as any);
          } catch { /* non-blocking */ }
        } else if (args.status === "rejected") {
          await supabase
            .from("bills")
            .update({
              status: "rejected",
              rejection_reason: args.note ?? "Rejected by approver",
              rejected_at: new Date().toISOString(),
              rejected_by: user!.id,
            } as any)
            .eq("id", linkedBillId);
          // Restore only the specific WO lines tied to this rejected bill
          try {
            const { data: rejectedBillLines } = await supabase
              .from("bill_lines")
              .select("work_order_line_id")
              .eq("bill_id", linkedBillId);
            const wolIds = (rejectedBillLines ?? [])
              .map((bl: any) => bl.work_order_line_id)
              .filter(Boolean);
            if (wolIds.length > 0) {
              // Get all OTHER bill_lines referencing these WO lines (excluding this rejected bill)
              const { data: otherBillLines } = await supabase
                .from("bill_lines")
                .select("work_order_line_id, bill_id")
                .in("work_order_line_id", wolIds)
                .neq("bill_id", linkedBillId);

              // For each, check if the other bill is in an active status
              const activeWolIds = new Set<string>();
              if (otherBillLines && otherBillLines.length > 0) {
                const otherBillIds = [...new Set((otherBillLines as any[]).map(bl => bl.bill_id))];
                const { data: activeBills } = await supabase
                  .from("bills")
                  .select("id")
                  .in("id", otherBillIds)
                  .not("status", "in", '("rejected","voided")');
                const activeBillIdSet = new Set((activeBills ?? []).map((b: any) => b.id));
                (otherBillLines as any[]).forEach(bl => {
                  if (activeBillIdSet.has(bl.bill_id)) {
                    activeWolIds.add(bl.work_order_line_id);
                  }
                });
              }

              // Restore only lines NOT tied to another active bill
              const restoreIds = wolIds.filter(id => !activeWolIds.has(id));
              if (restoreIds.length > 0) {
                await supabase
                  .from("work_order_lines")
                  .update({ status: "complete", billed_amount: 0 } as any)
                  .in("id", restoreIds);
              }
            } else {
              // No work_order_line_id traceability — log warning instead of blanket reset
              try {
                await supabase.from("ops_audit_log").insert({
                  table_name: "bills", record_id: linkedBillId,
                  action: "rejection_missing_traceability",
                  changed_by: user!.id,
                  metadata: { approval_request_id: args.requestId, note: "Bill lines lack work_order_line_id — manual WO line cleanup may be needed" },
                } as any);
              } catch { /* non-blocking */ }
            }
          } catch {
            // Non-blocking — don't fail the rejection if restore errors
          }
          try {
            await supabase.from("ops_audit_log").insert({
              table_name: "bills", record_id: linkedBillId,
              action: "bill_rejected_via_approval", prev_status: "sent", new_status: "rejected",
              changed_by: user!.id,
              metadata: { approval_request_id: args.requestId, rejection_reason: args.note },
            } as any);
          } catch { /* non-blocking */ }
        }
      }

      // Auto-dismiss any pending notifications linked to this approval request
      try {
        await supabase
          .from("notifications")
          .update({ status: "read" } as any)
          .eq("entity_type", "approval_request")
          .eq("entity_id", args.requestId)
          .eq("status", "unread");
      } catch { /* non-blocking */ }

      // Notify requester via approval_notifications (existing system)
      if (req?.requested_by) {
        await supabase.from("approval_notifications").insert({
          company_id: companyId,
          request_id: args.requestId,
          recipient_id: req.requested_by,
          notification_type: args.status,
        } as any);
      }

      // Also write to the main notifications table for the requester
      if (req?.requested_by) {
        try {
          const isApproved = args.status === "approved";
          await supabase.from("notifications").insert({
            company_id: companyId,
            assigned_to: req.requested_by,
            entity_type: "approval_request",
            entity_id: args.requestId,
            entity_label: (req as any)?.title ?? null,
            type: isApproved ? "approval_granted" : "approval_rejected",
            title: isApproved ? "Bill Approved" : "Bill Rejected",
            message: isApproved
              ? "Your bill has been approved."
              : `Your bill was rejected. Reason: ${args.note ?? "No reason given."}`,
            priority: isApproved ? "normal" : "high",
            status: "unread",
            action_url: null,
            created_by: user!.id,
          } as any);
        } catch {
          // Don't block main action if notification write fails
        }
      }
    },
    onSuccess: () => {
      invalidateAll();
      // Cascade invalidations for bill status + WO lines
      qc.invalidateQueries({ queryKey: ["bills"] });
      qc.invalidateQueries({ queryKey: ["wo_bills"] });
      qc.invalidateQueries({ queryKey: ["work_order_lines"] });
      qc.invalidateQueries({ queryKey: ["project_work_orders"] });
      qc.invalidateQueries({ queryKey: ["project_expenses"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const forward = useMutation({
    mutationFn: async (args: {
      assigneeId: string;
      requestId: string;
      forwardToUserId: string;
      note?: string;
    }) => {
      const companyId = await getCompanyId();
      // Mark current as forwarded
      await supabase.from("approval_assignees").update({
        status: "forwarded",
        forwarded_to: args.forwardToUserId,
        decided_at: new Date().toISOString(),
      }).eq("id", args.assigneeId);

      // Create new assignee
      await supabase.from("approval_assignees").insert({
        company_id: companyId,
        request_id: args.requestId,
        user_id: args.forwardToUserId,
      } as any);

      // Audit
      await supabase.from("approval_audit").insert({
        company_id: companyId,
        request_id: args.requestId,
        actor_id: user!.id,
        action: "forwarded",
        note: args.note,
        metadata: { forwarded_to: args.forwardToUserId },
      } as any);

      // Notify forwarded user
      await supabase.from("approval_notifications").insert({
        company_id: companyId,
        request_id: args.requestId,
        recipient_id: args.forwardToUserId,
        notification_type: "forwarded",
      } as any);
    },
    onSuccess: invalidateAll,
  });

  const withdraw = useMutation({
    mutationFn: async (args: { requestId: string }) => {
      const companyId = await getCompanyId();
      await supabase.from("approval_requests")
        .update({ status: "withdrawn", resolved_at: new Date().toISOString() })
        .eq("id", args.requestId);

      // Mark all assignees as withdrawn
      await supabase.from("approval_assignees")
        .update({ status: "withdrawn" })
        .eq("request_id", args.requestId)
        .eq("status", "pending");

      await supabase.from("approval_audit").insert({
        company_id: companyId,
        request_id: args.requestId,
        actor_id: user!.id,
        action: "withdrawn",
      } as any);
    },
    onSuccess: invalidateAll,
  });

  const sendForApproval = useMutation({
    mutationFn: async (args: {
      requestType: ApprovalRequestType;
      title: string;
      description?: string;
      projectId?: string;
      linkedWoId?: string;
      linkedDrawId?: string;
      linkedInvoiceId?: string;
      linkedSovLineId?: string;
      requestData?: Record<string, any>;
      urgency?: "normal" | "urgent";
      approvalMode?: "any" | "all";
      approverIds: string[];
      note?: string;
    }) => {
      const companyId = await getCompanyId();

      // Create request
      const { data: req, error: reqErr } = await supabase
        .from("approval_requests")
        .insert({
          company_id: companyId,
          project_id: args.projectId ?? null,
          request_type: args.requestType,
          title: args.title,
          description: args.description ?? null,
          linked_wo_id: args.linkedWoId ?? null,
          linked_draw_id: args.linkedDrawId ?? null,
          linked_invoice_id: args.linkedInvoiceId ?? null,
          linked_sov_line_id: args.linkedSovLineId ?? null,
          request_data: args.requestData ?? {},
          requested_by: user!.id,
          urgency: args.urgency ?? "normal",
          approval_mode: args.approvalMode ?? "any",
        } as any)
        .select()
        .single();
      if (reqErr) throw reqErr;

      // Create assignees
      const assignees = args.approverIds.map((uid) => ({
        company_id: companyId,
        request_id: req.id,
        user_id: uid,
      }));
      const { error: aaErr } = await supabase
        .from("approval_assignees")
        .insert(assignees as any);
      if (aaErr) throw aaErr;

      // Audit
      await supabase.from("approval_audit").insert({
        company_id: companyId,
        request_id: req.id,
        actor_id: user!.id,
        action: "created",
        note: args.note,
      } as any);

      // Notify approvers
      const notifs = args.approverIds.map((uid) => ({
        company_id: companyId,
        request_id: req.id,
        recipient_id: uid,
        notification_type: "request_received",
      }));
      await supabase.from("approval_notifications").insert(notifs as any);

      return req;
    },
    onSuccess: invalidateAll,
  });

  return { decide, forward, withdraw, sendForApproval };
};

// ─── Realtime notifications for requester ───
export const useApprovalRealtimeToasts = (onNotification: (payload: any) => void) => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("approval-notifs-toast")
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "approval_notifications",
        filter: `recipient_id=eq.${user.id}`,
      }, (payload) => {
        onNotification(payload.new);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, onNotification]);
};

// ─── Users who can approve a given request type ───
const APPROVAL_PERMISSION_MAP: Record<string, string[]> = {
  rate_change: ["admin", "controller"],
  low_margin: ["admin", "controller"],
  retainage_release: ["admin", "controller"],
  change_order: ["admin", "controller"],
  draw_approve: ["admin", "controller", "project_manager"],
  invoice_approve: ["admin", "controller"],
  bill_approve: ["admin", "controller"],
  void_record: ["admin", "controller"],
  reopen_wo: ["admin", "controller"],
  backcharge: ["admin", "controller"],
  vendor_approve: ["admin", "controller"],
  other: ["admin", "controller"],
};

export const useEligibleApprovers = (requestType: ApprovalRequestType) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["eligible_approvers", requestType],
    queryFn: async () => {
      const allowedRoles = APPROVAL_PERMISSION_MAP[requestType] ?? ["admin", "controller"];
      const { data, error } = await supabase
        .from("user_profiles")
        .select("user_id, first_name, last_name, display_name, role:roles(name)")
        .eq("status", "active");
      if (error) throw error;
      return (data ?? []).map((u: any) => ({
        userId: u.user_id,
        firstName: u.first_name,
        lastName: u.last_name,
        displayName: u.display_name ?? `${u.first_name} ${u.last_name}`,
        roleName: u.role?.name ?? "",
        canApprove: allowedRoles.includes((u.role?.name ?? "").toLowerCase()),
        isSelf: u.user_id === user?.id,
      }));
    },
    enabled: !!user,
  });
};
